import { createTheme, ThemeProvider } from '@mui/material/styles';
import { buttonClasses } from '@mui/material/Button';
import { tableRowClasses } from '@mui/material/TableRow';

declare module '@mui/material/styles' {
  interface BreakpointOverrides {
    xxl: true;
  }

  interface Palette {
    neutral: Palette['primary'];
  }

  interface PaletteOptions {
    neutral?: Palette['primary'];
  }

  interface TypographyVariants {
    controlLabel: React.CSSProperties;
  }

  interface TypographyVariantsOptions {
    controlLabel?: React.CSSProperties;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    controlLabel: true;
  }
}

export const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 576,
      md: 768,
      lg: 992,
      xl: 1200,
      xxl: 1400,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          padding: '6px 12px',
          fontFamily: 'NeoSansStd',
          borderRadius: 29,
          boxShadow: 'none',
          color: '#fff',
          textTransform: 'uppercase',
          [`&.${buttonClasses.disabled}`]: {
            background: '#E5E5E5',
            color: '#ADADAD',
          },
        },
        outlined: {
          background: 'transparent',
          borderWidth: 2,
          '&:hover': {
            borderWidth: 2,
          },
        },
        outlinedPrimary: ({ theme }) => ({
          color: theme.palette.primary.main,
          borderColor: theme.palette.primary.main,
        }),
        outlinedSecondary: ({ theme }) => ({
          color: theme.palette.secondary.main,
          borderColor: theme.palette.secondary.main,
        }),
        containedInfo: ({ theme }) => ({
          color: theme.palette.secondary.main,
          '&:hover': {
            background: theme.palette.info.main,
            color: theme.palette.secondary.dark,
          },
        }),
      },
      defaultProps: {
        color: 'primary',
        variant: 'contained',
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: ({ theme }) => ({
          margin: 0,
          padding: 0,
          fontFamily: 'NeoSansStd',
          fontSize: theme.typography.pxToRem(24),
          fontWeight: 600,
          color: theme.palette.primary.main,
        }),
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: 'auto',
          padding: '3px',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          background: '#fff',
          [`&.${tableRowClasses.hover}`]: {
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: 'rgb(247, 247, 247)',
            },
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          padding: '12px 10px',
          fontSize: theme.typography.pxToRem(14),
          fontFamily: 'NeoSansStd',
        }),
        head: {
          background: '#8293a3',
          color: '#fff',
        },
        body: {
          background: 'transparent',
          color: '#224060',
        },
      },
    },
    MuiTabs: {
      defaultProps: {
        scrollButtons: 'auto',
        variant: 'scrollable',
      },
      styleOverrides: {
        root: {
          position: 'relative',
          display: 'inline-flex',
          maxWidth: '100%',
          boxShadow: '0px 5px 8px #7088D234',
          '& > div:first-child': {
            left: 0,
          },
          '& > div:last-child': {
            right: 0,
          },
        },
        indicator: {
          display: 'none',
        },
        scrollButtons: {
          position: 'absolute',
          maxHeight: 58,
          height: '100%',
          background: '#EFF2F7',
          opacity: 1,
          zIndex: 9,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: ({ theme }) => ({
          display: 'flex',
          flexDirection: 'row-reverse',
          alignItems: 'center',
          padding: 15,
          fontSize: 16,
          fontFamily: 'NeoSansStd',
          fontWeight: 600,
          lineHeight: 1.75,
          background: '#fff',
          borderRight: '1px solid rgba(112, 112, 112, .24)',
          color: theme.palette.primary.main,
          cursor: 'pointer',
          textTransform: 'none',
          opacity: 1,
          maxWidth: '100%',
          [theme.breakpoints.up('sm')]: {
            minWidth: 'auto',
          },
          '&:last-child': {
            borderRight: 0,
          },

          '&.Mui-selected': {
            background: theme.palette.primary.main,
            border: 0,
            color: '#fff',
            cursor: 'default',
          },
        }),
        labelIcon: ({ theme }) => ({
          minHeight: 'auto',
          '& .MuiTab-wrapper > *:first-child': {
            marginLeft: 10,
            marginBottom: 0,
            color: theme.palette.warning.main,
          },
        }),
        textColorInherit: {
          opacity: 1,
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        variantMapping: {
          controlLabel: 'span',
        },
      },
    },
  },

  palette: {
    primary: {
      main: 'rgb(34, 64, 96)',
    },
    secondary: {
      main: '#347AC4',
    },
    success: {
      main: '#218D7A',
    },
    warning: {
      main: 'rgba(219, 118, 0, 1)',
    },
    error: {
      main: '#A51010',
    },
    info: {
      main: '#fff',
    },
    neutral: {
      main: 'rgb(126, 132, 131)',
      contrastText: 'rgb(126, 132, 131)',
      dark: 'rgb(126, 132, 131)',
      light: 'rgb(126, 132, 131)',
    },
  },

  typography: {
    controlLabel: {
      fontFamily: '0.9375rem',
      color: '#fff',
    },
  },
});

export type Theme = typeof theme;
export { ThemeProvider };
